import { useEffect, useState } from "react";
import { createBrowserRouter, Link, RouterProvider, useNavigate } from "react-router";

import { CALLBACK_PATH, completeSignIn } from "./auth/oidc";
import { useSession } from "./auth/session";
import { EmptyState } from "./components/ui";
import { ContactRecord } from "./pages/contact/ContactRecord";
import { Contacts } from "./pages/Contacts";
import { Home } from "./pages/Home";
import { LeadRecord } from "./pages/LeadRecord";
import { Leads } from "./pages/Leads";
import { Policies } from "./pages/Policies";
import { PolicyRecord } from "./pages/PolicyRecord";
import { SignIn } from "./pages/SignIn";
import { Tasks } from "./pages/Tasks";
import { UnmatchedCalls } from "./pages/UnmatchedCalls";
import { VendorHome } from "./pages/Vendor";
import { Shell } from "./shell/Shell";

function NotFound() {
  return (
    <div className="page">
      <EmptyState icon="search" title="Nothing at this address">
        It may have been deleted, or the link is wrong. <Link to="/">Back to Home</Link>
      </EmptyState>
    </div>
  );
}

/** The identity provider sends people back here with a code; trade it, then go where they were going. */
function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    completeSignIn(location.href)
      .then((to) => {
        // A full reload, so the session starts from the stored tokens rather than half-built state.
        location.replace(to);
      })
      .catch((err: Error) => setError(err.message));
  }, [navigate]);
  return error ? <SignIn reason={error} /> : <div className="gate" aria-busy="true" />;
}

/**
 * Signed in: the CRM, or for the lead vendor its one page. Not: the sign-in screen, at whatever
 * address was asked for.
 */
function Gate() {
  const { state } = useSession();
  if (state.status === "loading") return <div className="gate" aria-busy="true" />;
  if (state.status === "signed-out") return <SignIn reason={state.reason} />;
  if (state.me.user.role === "lead_vendor") return <VendorHome />;
  return <Shell />;
}

const router = createBrowserRouter([
  { path: CALLBACK_PATH, element: <AuthCallback /> },
  {
    element: <Gate />,
    children: [
      { index: true, element: <Home /> },
      { path: "leads", element: <Leads /> },
      { path: "leads/:id", element: <LeadRecord /> },
      { path: "contacts", element: <Contacts /> },
      { path: "contacts/:id", element: <ContactRecord /> },
      { path: "policies", element: <Policies /> },
      { path: "policies/:id", element: <PolicyRecord /> },
      // The pages a seller opens least load on first visit, so the floor's first paint stays small.
      {
        path: "calendar",
        lazy: () => import("./pages/Calendar").then((m) => ({ Component: m.Calendar })),
      },
      {
        path: "dashboards",
        lazy: () =>
          import("./pages/dashboards/Dashboards").then((m) => ({ Component: m.Dashboards })),
      },
      {
        path: "reports",
        lazy: () => import("./pages/Reports").then((m) => ({ Component: m.Reports })),
      },
      {
        path: "commissions",
        lazy: () => import("./pages/Commissions").then((m) => ({ Component: m.Commissions })),
      },
      { path: "admin", lazy: () => import("./pages/Admin").then((m) => ({ Component: m.Admin })) },
      {
        path: "pipeline",
        lazy: () => import("./pages/Pipeline").then((m) => ({ Component: m.Pipeline })),
      },
      { path: "tasks", element: <Tasks /> },
      { path: "calls", element: <UnmatchedCalls /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
